import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { ChevronDown, ChevronUp, TrendingUp, DollarSign, PieChart, Calendar, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

function formatCurrency(value) {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function formatDateForDisplay(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDateRangeLabel(startDate, endDate, activePreset) {
  if (activePreset) {
    return `Last ${activePreset} Days`;
  }
  if (startDate && endDate) {
    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
  }
  if (startDate) {
    return `From ${formatDateForDisplay(startDate)}`;
  }
  if (endDate) {
    return `Until ${formatDateForDisplay(endDate)}`;
  }
  return "All Time";
}

export default function FinancialSummaryStrip({ filterStatus }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Date range state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, [filterStatus, startDate, endDate]);

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== "all") params.append("status", filterStatus);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const res = await apiClient.get(`/programs/financial-summary${queryString}`);
      setData(res.data);
    } catch (err) {
      console.error("Failed to load financial summary");
    }
    setLoading(false);
  };

  const setPresetRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setActivePreset(days);
  };

  const clearDateRange = () => {
    setStartDate("");
    setEndDate("");
    setActivePreset(null);
  };

  const applyCustomRange = () => {
    setActivePreset(null);
    setDatePickerOpen(false);
  };

  if (loading || !data) {
    return (
      <Card className="mb-6 animate-pulse">
        <CardContent className="py-4">
          <div className="h-12 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  const hasBreakdown = data.scope_breakdown;
  const hasDateFilter = startDate || endDate;

  return (
    <Card className="border border-border mb-6" data-testid="programs-financial-summary">
      <CardContent className="py-4">
        <div className="flex flex-col gap-4">
          {/* Date Range Filter Row */}
          <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-border">
            <span className="text-xs font-medium text-muted-foreground mr-1">Period:</span>
            
            {/* Quick Preset Buttons */}
            <div className="flex gap-1">
              <Button
                type="button"
                variant={activePreset === 30 ? "default" : "outline"}
                size="sm"
                onClick={() => setPresetRange(30)}
                className="h-7 px-2.5 text-xs"
                data-testid="date-preset-30"
              >
                30d
              </Button>
              <Button
                type="button"
                variant={activePreset === 60 ? "default" : "outline"}
                size="sm"
                onClick={() => setPresetRange(60)}
                className="h-7 px-2.5 text-xs"
                data-testid="date-preset-60"
              >
                60d
              </Button>
              <Button
                type="button"
                variant={activePreset === 90 ? "default" : "outline"}
                size="sm"
                onClick={() => setPresetRange(90)}
                className="h-7 px-2.5 text-xs"
                data-testid="date-preset-90"
              >
                90d
              </Button>
            </div>
            
            <div className="h-5 w-px bg-border mx-1" />
            
            {/* Custom Date Range Picker */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={!activePreset && hasDateFilter ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-2.5 text-xs gap-1.5"
                  data-testid="date-range-custom"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {!activePreset && hasDateFilter ? getDateRangeLabel(startDate, endDate, null) : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="flex flex-col gap-3">
                  <p className="text-xs font-medium text-muted-foreground">Custom Date Range</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-muted-foreground mb-1 block">From</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
                        className="h-8 text-xs"
                        data-testid="date-range-start"
                      />
                    </div>
                    <span className="text-muted-foreground mt-4">→</span>
                    <div className="flex-1">
                      <label className="text-[10px] text-muted-foreground mb-1 block">To</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
                        className="h-8 text-xs"
                        data-testid="date-range-end"
                      />
                    </div>
                  </div>
                  <Button size="sm" onClick={applyCustomRange} className="h-7 text-xs">
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Clear Button */}
            {hasDateFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDateRange}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                data-testid="date-range-clear"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
            
            {/* Active Range Label */}
            {hasDateFilter && (
              <span className="ml-auto text-xs text-muted-foreground hidden sm:block">
                Showing: <span className="font-medium text-foreground">{getDateRangeLabel(startDate, endDate, activePreset)}</span>
              </span>
            )}
          </div>

          {/* Main Summary Row */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-x-10 gap-y-6">
            {/* Numbers are heroes: display numeral, caps micro-label above,
                hairline rules between columns. Full currency needs real width,
                so the columns stack into hairline-separated rows on a phone. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:flex lg:items-baseline min-w-0 flex-1
                            divide-y divide-border sm:divide-y-0 sm:divide-x">
              <div className="py-2.5 sm:py-0 sm:pr-4 lg:pr-10">
                <p className="micro-label mb-1.5">Contracted</p>
                <p className="text-xl lg:text-2xl tabular-nums">{formatCurrency(data.total_contracted)}</p>
              </div>
              <div className="py-2.5 sm:py-0 sm:px-4 lg:px-10">
                <p className="micro-label mb-1.5">Collected</p>
                <p className="text-xl lg:text-2xl tabular-nums text-green-600">{formatCurrency(data.total_collected)}</p>
              </div>
              <div className="py-2.5 sm:py-0 sm:px-4 lg:px-10">
                <p className="micro-label mb-1.5">Outstanding</p>
                <p className="text-xl lg:text-2xl tabular-nums text-terracotta">{formatCurrency(data.total_outstanding)}</p>
              </div>
            </div>

            {/* Collection rule */}
            <div className="w-full shrink-0 lg:w-[220px]">
              <div className="flex items-baseline justify-between mb-2">
                <span className="micro-label">Collected</span>
                <span className="text-sm tabular-nums text-foreground">{data.collection_rate}%</span>
              </div>
              <div className="h-1 bg-warm-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-estate rounded-full transition-transform duration-550 ease-brand"
                  style={{ width: `${Math.min(data.collection_rate, 100)}%` }}
                />
              </div>
            </div>

            {/* Expand Button - only if we have breakdown data */}
            {hasBreakdown && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="shrink-0"
              >
                <PieChart className="w-4 h-4 mr-1" />
                {expanded ? "Less" : "Details"}
                {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
            )}
          </div>

          {/* Expanded Revenue Breakdown */}
          {expanded && hasBreakdown && (
            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Revenue by Scope
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Visualization</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(data.scope_breakdown.visualization_revenue)}</p>
                  <p className="text-xs text-blue-500">{data.scope_breakdown.visualization_percentage}% of total</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3">
                  <p className="text-xs text-pink-600 font-medium">Marketing</p>
                  <p className="text-lg font-bold text-pink-700">{formatCurrency(data.scope_breakdown.marketing_revenue)}</p>
                  <p className="text-xs text-pink-500">{data.scope_breakdown.marketing_percentage}% of total</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-medium">VEXOS</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(data.scope_breakdown.vexos_revenue)}</p>
                  <p className="text-xs text-purple-500">{data.scope_breakdown.vexos_percentage}% of total</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
